import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, of } from 'rxjs';
import { startWith, map, finalize, takeUntil } from 'rxjs/operators';
import { Mobile_Apps } from 'app/models/Mobile_Apps';
import { MobileAppsService } from 'app/services/mobile-apps/mobile-apps.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import { AgenciesService } from 'app/services/agencies/agencies.service';

@Component({
  selector: 'app-modifier-mobile-apps',
  templateUrl: './modifier-mobile-apps.component.html',
  styleUrls: ['./modifier-mobile-apps.component.scss']
})
export class ModifierMobileAppsComponent implements OnInit, OnDestroy {
  @ViewChild('appForm') appForm!: NgForm;

  public agencieAppData: Mobile_Apps = new Mobile_Apps();
  public isEditMode = true;
  public isLoading = false;
  public isUploading = false;
  public uploadProgress = 0;
  public imagePreviewUrl: SafeUrl | null = null;
  public uploadedImageUrl: string | null = null;

  public agencies: any[] = [];
  public filteredAgencies: Observable<any[]> = of([]);
  public agencySearchControl: string = '';

  private destroy$ = new Subject<void>();
  private appId: any;

  constructor(
    private mobileAppService: MobileAppsService,
    private agenciesService: AgenciesService,
    private awsService: AwscontrolService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.appId = this.route.snapshot.paramMap.get('id');
    if (!this.appId) {
      this.showNotification('ID de l\'application manquant', 'error');
      this.router.navigate(['/typography']);
      return;
    }
    this.loadAppData();
    this.loadAgencies();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (typeof this.imagePreviewUrl === 'string' && !this.imagePreviewUrl.startsWith('http')) {
      URL.revokeObjectURL(this.imagePreviewUrl);
    }
  }

  loadAppData(): void {
    this.mobileAppService.getApp(this.appId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.agencieAppData = data;
          this.agencySearchControl = data.agencyName || '';
          this.uploadedImageUrl = data.mobile_cover_image_url || null;
          if (data.mobile_cover_image_url) {
            this.imagePreviewUrl = this.sanitizer.bypassSecurityTrustUrl(data.mobile_cover_image_url);
          }
          this.updateFilteredAgencies();
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('loadAppData error', err);
          this.showNotification('Erreur chargement application', 'error');
        }
      });
  }

  loadAgencies(): void {
    this.agenciesService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          if (Array.isArray(data)) this.agencies = data;
          else if (data?.data && Array.isArray(data.data)) this.agencies = data.data;
          else if (data?.items && Array.isArray(data.items)) this.agencies = data.items;
          else this.agencies = [];
          this.updateFilteredAgencies();
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('loadAgencies error', err);
          this.showNotification('Erreur chargement agences', 'error');
        }
      });
  }

  private updateFilteredAgencies(): void {
    this.filteredAgencies = of(this.agencySearchControl).pipe(
      startWith(''),
      map(search => this._filterAgencies(search))
    );
  }

  private _filterAgencies(value: string): any[] {
    const filterValue = value?.toLowerCase().trim() || '';
    if (!filterValue) return this.agencies;
    return this.agencies.filter(agency =>
      agency.name?.toLowerCase().includes(filterValue)
    );
  }

  onAgencySearchChange(searchTerm: string): void {
    this.agencySearchControl = searchTerm;
    this.updateFilteredAgencies();
  }

  onAgencySelected(event: MatAutocompleteSelectedEvent): void {
    const selectedAgency = event.option.value;
    this.agencieAppData.agencyName = selectedAgency.name;
    this.agencySearchControl = selectedAgency.name;
    this.updateFilteredAgencies();
  }

  displayAgencyFn(agency: any): string {
    return agency?.name ?? '';
  }

  // ==================== IMAGE UPLOAD ====================
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      this.showNotification('Format non supporté (JPG, PNG, WEBP)', 'error');
      return;
    }
    if (file.size > maxSize) {
      this.showNotification('L\'image ne doit pas dépasser 5 Mo', 'error');
      return;
    }

    if (typeof this.imagePreviewUrl === 'string' && !this.imagePreviewUrl.startsWith('http')) {
      URL.revokeObjectURL(this.imagePreviewUrl);
    }

    const blobUrl = URL.createObjectURL(file);
    this.imagePreviewUrl = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
    this.cdr.detectChanges();

    this.uploadImage(file);
  }

  private uploadImage(file: File): void {
    this.isUploading = true;
    this.uploadProgress = 0;

    const progressInterval = setInterval(() => {
      if (this.uploadProgress < 90) {
        this.uploadProgress += 10;
        this.cdr.detectChanges();
      }
    }, 200);

    this.awsService.uploadFile(file, 'mobile-apps')
      .pipe(finalize(() => {
        clearInterval(progressInterval);
        this.isUploading = false;
        this.uploadProgress = 100;
        setTimeout(() => { this.uploadProgress = 0; this.cdr.detectChanges(); }, 1000);
      }))
      .subscribe({
        next: (response: any) => {
          const url = response.url || (response.urls && response.urls[0]);
          if (url) {
            this.uploadedImageUrl = url;
            this.agencieAppData.mobile_cover_image_url = url;
            this.showNotification('Image téléchargée avec succès', 'success');
          } else {
            this.showNotification('Erreur : URL non reçue', 'error');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.showNotification('Erreur lors du téléchargement', 'error');
          this.isUploading = false;
        }
      });
  }

  // ==================== MISE À JOUR ====================
  onSubmit(form: NgForm): void {
    const payload: any = {
      name: this.agencieAppData.name || null,
      agencyName: this.agencieAppData.agencyName || null,
      playstore_url: this.agencieAppData.playstore_url || null,
      appstore_url: this.agencieAppData.appstore_url || null,
      mobile_cover_image_url: this.uploadedImageUrl || this.agencieAppData.mobile_cover_image_url || null
    };

    console.log('📤 Sending update payload:', payload);

    this.isLoading = true;
    this.mobileAppService.updateApp(this.appId, payload)
      .pipe(finalize(() => { this.isLoading = false; this.cdr.detectChanges(); }))
      .subscribe({
        next: (response: string) => {
          // ✅ Ici, response est la chaîne "Application X updated with success !"
          console.log('✅ Update success:', response);
          this.showNotification('Application mise à jour avec succès', 'success');
          this.router.navigate(['/typography']);
        },
        error: (err: Error) => {
          console.error('❌ Update error:', err.message);
          this.showNotification(`Erreur: ${err.message}`, 'error');
        }
      });
  }

  cancel(): void {
    this.router.navigate(['/typography']);
  }

  private showNotification(message: string, type: 'success' | 'error' | 'warning'): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 4000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: `notification-${type}`
    });
  }
}